<script setup>
// SiteUp: seletor de ferramentas (tools) do agente + pacotes de nicho.
// - Pacotes de nicho (gating por CONTA): tools especializadas (ex.: Energia
//   Solar) só ficam disponíveis se a conta ativar o pacote. Afeta toda a conta.
// - Seleção de tools (por AGENTE): dentre as disponíveis, quais este agente usa
//   (config.enabled_tool_ids). faq_lookup e handoff são essenciais (sempre on).
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import { apresentarAcao } from 'dashboard/helper/captainToolsApresentacao';

const props = defineProps({
  assistantId: { type: Number, required: true },
});

const CORE_TOOL_IDS = ['faq_lookup', 'handoff'];

const route = useRoute();
const store = useStore();

const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const assistantId = computed(() => props.assistantId);
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);

// As 4 tools de imovel nao vivem no catalogo (as classes recebem account:/assistant:, e o
// catalogo instancia com .new(self)), entao elas entram como um bloco proprio. A conta
// libera pela feature; aqui o AGENTE decide se usa. Desligado = 4 schemas a menos no prompt
// de cada sugestao daquele agente.
const PROPERTY_TOOL_TITLES = [
  'Buscar imoveis',
  'Detalhes do imovel',
  'Simular financiamento',
  'Captacao de imovel',
];

const tools = ref([]);
const propertyToolsEnabled = ref(true);
const packs = ref([]);
const selected = ref(new Set());
const isFetching = ref(false);
const isSavingPacks = ref(false);
const isSaving = computed(() => uiFlags.value.updatingItem);

const showPropertyTools = computed(() =>
  isFeatureEnabledonAccount.value(
    Number(route.params.accountId),
    FEATURE_FLAGS.IMOVEIS_SUGGESTION
  )
);

const materialsUrl = computed(() => ({
  name: 'captain_assistants_materials_index',
  params: { accountId: route.params.accountId, assistantId: assistantId.value },
}));

// send_material ativa sem nenhum material em config.materials = tool inútil
// (incidente real: o contrato não era enviado só porque faltava o material).
// config só vem pra admin no serializer — sem config, não dá pra afirmar nada.
const showSendMaterialHint = computed(() => {
  if (!selected.value.has('send_material')) return false;
  const config = assistant.value?.config;
  if (!config) return false;
  return !(config.materials?.length > 0);
});

const isCore = id => CORE_TOOL_IDS.includes(id);
const apresentacoes = computed(() =>
  tools.value.map(tool => ({ tool, humana: apresentarAcao(tool) }))
);

const fetchTools = async () => {
  isFetching.value = true;
  try {
    const { data } = await CaptainAssistantAPI.getTools();
    tools.value = data || [];
  } catch {
    useAlert('Não foi possível carregar as ferramentas.');
  } finally {
    isFetching.value = false;
  }
};

// Quando um pacote/tool-de-conta liga/desliga, a lista de tools DISPONÍVEIS muda.
// Removemos da seleção os ids que deixaram de existir (mantendo os CORE), senão
// um id órfão (de uma tool agora indisponível) seria persistido no próximo Salvar
// como enabled_tool_id fantasma. Só roda após a seleção já ter sido inicializada.
const reconcileSelection = () => {
  if (selected.value.size === 0) return;
  const availableIds = new Set(tools.value.map(tool => tool.id));
  const next = new Set(
    [...selected.value].filter(id => isCore(id) || availableIds.has(id))
  );
  selected.value = next;
};

const fetchToolsAndReconcile = async () => {
  await fetchTools();
  reconcileSelection();
};

const fetchPacks = async () => {
  try {
    const { data } = await CaptainAssistantAPI.getToolPacks();
    packs.value = data || [];
  } catch {
    // sem pacotes definidos — segue só com o núcleo
  }
};

const togglePack = async pack => {
  isSavingPacks.value = true;
  const target = !pack.enabled;
  try {
    const enabledIds = packs.value
      .map(p => (p.id === pack.id ? { ...p, enabled: target } : p))
      .filter(p => p.enabled)
      .map(p => p.id);
    await CaptainAssistantAPI.updateToolPacks(enabledIds);
    pack.enabled = target;
    // As tools disponíveis mudam quando um pacote liga/desliga — recarrega a
    // lista e poda da seleção os ids que sumiram (sem deixar órfão).
    await fetchToolsAndReconcile();
    useAlert('Pacotes atualizados.');
  } catch {
    useAlert('Não foi possível atualizar os pacotes.');
  } finally {
    isSavingPacks.value = false;
  }
};

const initSelection = () => {
  const enabled = assistant.value?.effective_tool_ids || [];
  selected.value = new Set([...enabled, ...CORE_TOOL_IDS]);
  // Ausente = ligado, igual ao backend: agente ja configurado nao perde a busca de imovel
  // so porque esta tela passou a existir.
  propertyToolsEnabled.value =
    assistant.value?.config?.property_tools_enabled !== false;
};

const toggle = id => {
  if (isCore(id)) return;
  const next = new Set(selected.value);
  next[next.has(id) ? 'delete' : 'add'](id);
  selected.value = next;
};

const save = async () => {
  try {
    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      config: {
        enabled_tool_ids: Array.from(selected.value),
        ...(showPropertyTools.value
          ? { property_tools_enabled: propertyToolsEnabled.value }
          : {}),
      },
    });
    useAlert('Ferramentas atualizadas.');
  } catch {
    useAlert('Não foi possível salvar.');
  }
};

onMounted(async () => {
  await Promise.all([
    fetchTools(),
    fetchPacks(),
    // Rebuscar o assistente já carregado ligava o spinner do layout pai e
    // desmontava este painel em laço (QA da v981). Só busca se ainda não veio.
    assistant.value
      ? Promise.resolve()
      : store
          .dispatch('captainAssistants/show', assistantId.value)
          .catch(() => {}),
  ]);
  initSelection();
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- Textos herdados da tela de ferramentas (anteriores a 02/09/2026); a fatia 3
     trouxe a apresentação humana das ações via captainToolsApresentacao. -->
<template>
  <div class="flex flex-col gap-7" data-testid="painel-acoes">
    <div v-if="packs.length" class="flex flex-col gap-2">
      <h4 class="text-xs font-medium text-n-slate-12">
        Pacotes de nicho
        <span class="text-[10px] font-normal text-n-slate-10">
          (afeta a conta toda)
        </span>
      </h4>
      <p class="text-xs text-n-slate-11">
        Ativa conjuntos de ferramentas especializadas. Só aparecem na lista
        abaixo as ferramentas dos pacotes ativos.
      </p>
      <label
        v-for="pack in packs"
        :key="pack.id"
        class="flex items-center gap-3 rounded-xl border border-n-weak p-3 cursor-pointer"
      >
        <input
          type="checkbox"
          :checked="pack.enabled"
          :disabled="isSavingPacks"
          @change="togglePack(pack)"
        />
        <span class="text-sm font-medium text-n-slate-12">{{
          pack.title
        }}</span>
        <span class="text-xs text-n-slate-10">
          {{ pack.tool_ids.join(', ') }}
        </span>
      </label>
    </div>

    <div v-if="showPropertyTools" class="flex flex-col gap-2">
      <h4 class="text-xs font-medium text-n-slate-12">Busca de imoveis</h4>
      <p class="text-xs text-n-slate-11">
        Deixe ligado no agente que atende quem procura imovel. Em um agente de
        suporte, desligue: sao 4 ferramentas a menos em cada sugestao, o que
        deixa a resposta mais rapida e evita o cartao de imovel aparecer numa
        conversa que nao e de imovel.
      </p>
      <label
        class="flex items-start gap-3 rounded-xl border border-n-weak p-3 cursor-pointer"
      >
        <input v-model="propertyToolsEnabled" type="checkbox" class="mt-1" />
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-n-slate-12">
            Este agente busca imoveis
          </p>
          <p class="text-xs text-n-slate-11">
            {{ PROPERTY_TOOL_TITLES.join(', ') }}
          </p>
        </div>
      </label>
    </div>

    <div class="flex flex-col gap-2">
      <h4 class="text-xs font-medium text-n-slate-12">
        Ferramentas deste agente
      </h4>
      <label
        v-for="{ tool, humana } in apresentacoes"
        :key="tool.id"
        class="flex items-start gap-3 rounded-xl border border-n-weak p-3"
        :class="
          isCore(tool.id) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
        "
        :data-testid="`acao-${tool.id}`"
      >
        <input
          type="checkbox"
          class="mt-1"
          :checked="selected.has(tool.id)"
          :disabled="isCore(tool.id)"
          @change="toggle(tool.id)"
        />
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-n-slate-12">
            {{ humana.nome }}
            <span v-if="isCore(tool.id)" class="text-[10px] text-n-slate-10">
              (essencial)
            </span>
            <span
              v-if="humana.tecnica"
              class="ml-1 rounded-full border border-n-weak px-1.5 text-[10px] font-normal text-n-slate-10"
              >técnica</span
            >
          </p>
          <p class="text-xs text-n-slate-11">{{ humana.oQueFaz }}</p>
          <p v-if="humana.exemplo" class="text-xs italic text-n-slate-10">
            {{ humana.exemplo }}
          </p>
          <div
            v-if="tool.id === 'send_material' && showSendMaterialHint"
            class="mt-1.5 flex items-center gap-1.5 rounded-md bg-n-amber-3 px-2 py-1.5 text-xs text-n-amber-11"
          >
            <span class="i-ri-alert-line size-3.5 shrink-0" />
            <span>
              Ativa, mas nenhum material cadastrado: a IA não terá o que enviar.
              <router-link
                :to="materialsUrl"
                class="font-medium underline"
                @click.stop
              >
                Cadastrar materiais
              </router-link>
            </span>
          </div>
        </div>
      </label>
    </div>

    <div class="mt-6">
      <Button label="Salvar ferramentas" :is-loading="isSaving" @click="save" />
    </div>
  </div>
</template>
